import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Building, Shield, Lock } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../hooks/useAuth';
import UserManagement from '../UserManagement/UserManagement';
import RoleMaster from './RoleMaster';
import PermissionMaster from './PermissionMaster';
import { DepartmentMaster } from './MasterPages';

type TabKey = 'users' | 'departments' | 'roles' | 'permissions';

const TABS: { key: TabKey; label: string; icon: React.ElementType; permission: string | string[]; Component: React.FC }[] = [
    // users.view is a read permission that Audit pages also require — gate on
    // manage-level rights so read-only roles don't see this tab (mirrors the
    // old standalone "Users" nav item's requiredAnyPermission).
    { key: 'users', label: 'Users', icon: Users, permission: ['users.manage', 'users.create', 'users.edit'], Component: UserManagement },
    { key: 'departments', label: 'Departments', icon: Building, permission: 'departments.manage', Component: DepartmentMaster },
    { key: 'roles', label: 'Roles', icon: Shield, permission: 'roles.view', Component: RoleMaster },
    { key: 'permissions', label: 'Permissions', icon: Lock, permission: 'roles.view', Component: PermissionMaster },
];

const UserAccessSetupPage: React.FC = () => {
    const { hasPermission, hasAnyPermission } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const visibleTabs = useMemo(
        () => TABS.filter((t) => (Array.isArray(t.permission) ? hasAnyPermission(t.permission) : hasPermission(t.permission))),
        [hasPermission, hasAnyPermission]
    );

    const requestedTab = searchParams.get('tab') as TabKey | null;
    const activeTab = visibleTabs.some((t) => t.key === requestedTab) ? (requestedTab as TabKey) : visibleTabs[0]?.key;

    const handleTabChange = (value: string) => {
        setSearchParams({ tab: value });
    };

    if (!activeTab) {
        return null;
    }

    return (
        <div className="space-y-4 md:p-6">
            <div>
                <h1 className="text-2xl font-bold tracking-tight">User & Access Setup</h1>
                <p className="text-sm text-muted-foreground">Manage users, departments, roles, and permissions</p>
            </div>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="flex h-auto w-full flex-wrap justify-start sm:w-auto">
                    {visibleTabs.map((tab) => (
                        <TabsTrigger key={tab.key} value={tab.key} className="gap-2">
                            <tab.icon className="h-4 w-4" />
                            {tab.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {visibleTabs.map((tab) => (
                    <TabsContent key={tab.key} value={tab.key}>
                        <tab.Component />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
};

export default UserAccessSetupPage;
